import cx from 'classnames';
import * as React from 'react';

import { ButtonColorUnion } from 'components/Button';
import Image from 'components/Image';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';

import './Footer.scss';

type FooterNavColumnLinks = {
  color?: ButtonColorUnion;
  copy: string;
  external?: boolean;
  image?: string;
  to: string;
};

type FooterNavColumnType = {
  className?: string;
  links: Array<FooterNavColumnLinks>;
  title?: string;
};

const FooterNavColumn = ({ className, links, title }: FooterNavColumnType) => (
  <section className={cx('Footer-nav-column', className)}>
    {title && (
      <Text element="h4" smallCaps weight="semibold" className="Footer-nav-column-title">
        {title}
      </Text>
    )}
    <ul>
      {links.map(item => (
        <li key={item.to} className="Footer-nav-list-item">
          <Link href={item.to} color={item.color} target={item.external ? '_blank' : undefined}>
            <Text weight="semibold">
              {item.image && <Image src={item.image} className="Footer-nav-list-item-icon" />}
              {item.copy}
            </Text>
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

type Props = {
  disclaimer?: React.ReactNode;
};

export default function Footer({ disclaimer }: Props) {
  const socials = [
    {
      name: 'Facebook',
      image: require('assets/images/Footer/socials/facebook.svg'),
      to: LinkConstants.FACEBOOK,
    },
    {
      name: 'Twitter',
      image: require('assets/images/Footer/socials/twitter.svg'),
      to: LinkConstants.TWITTER,
    },
    {
      name: 'Instagram',
      image: require('assets/images/Footer/socials/instagram.svg'),
      to: LinkConstants.INSTAGRAM,
    },
  ];

  const iKeepSafe = (
    <Text size="xsmall" weight="semibold" wrap={false} className="Footer-ikeepsafe">
      Certified by
      <Image
        src={require('assets/images/Footer/ikeepsafe.svg')}
        alt="iKeepSafe"
        className="Footer-ikeepsafe-brand"
      />
    </Text>
  );

  return (
    <footer className="Footer">
      {disclaimer && (
        <div className="Footer-disclaimer Footer-container">
          <Row size="medium">
            <Text color="eclipse" element="p" flush size="xxxsmall">
              {disclaimer}
            </Text>
          </Row>
        </div>
      )}
      <div className="Footer-container--main Footer-container">
        <nav className="Footer-nav">
          <FooterNavColumn
            className="Footer-nav-column--terms"
            links={[
              {
                color: 'violet',
                image: require('assets/images/Footer/lock.svg'),
                copy: 'Terms & Policies',
                to: LinkConstants.TERMS_OF_SERVICE,
              },
            ]}
          />
          <FooterNavColumn
            title="Product"
            links={[
              {
                copy: 'Schools & Districts',
                to: LinkConstants.PLANS,
              },
              {
                copy: 'Higher Education',
                to: LinkConstants.HIGHER_EDUCATION,
              },
              {
                copy: 'Teachers',
                to: LinkConstants.TEACHERS,
              },
              {
                copy: 'Families',
                to: LinkConstants.FAMILIES,
              },
              {
                copy: 'Partners',
                to: LinkConstants.PARTNERS,
              },
            ]}
          />
          <FooterNavColumn
            title="Company"
            links={[
              {
                copy: 'About Us',
                to: LinkConstants.ABOUT,
              },
              {
                copy: 'Careers',
                to: LinkConstants.CAREERS,
              },
              {
                copy: 'Press',
                to: LinkConstants.PRESS,
              },
              {
                copy: 'Resources',
                to: LinkConstants.RESOURCES,
              },
            ]}
          />
          <FooterNavColumn
            title="Community"
            links={[
              {
                copy: 'Engagement Ratings',
                to: LinkConstants.ENGAGEMENT_RATINGS,
              },
              {
                copy: 'Connected Educators',
                to: LinkConstants.COMMUNITY,
              },
              {
                copy: 'Trust & Safety',
                to: LinkConstants.TRUST_SAFETY,
              },
              {
                copy: 'Help Center',
                to: LinkConstants.HELP,
                external: true,
              },
              {
                copy: 'Blog',
                to: LinkConstants.BLOG,
              },
            ]}
          />
        </nav>
        <div className="Footer-secondary">
          <div className="Footer-socialsWrap">
            <div className="Footer-socials">
              {socials.map(item => (
                <Link
                  href={item.to}
                  key={item.name}
                  target="_blank"
                  className="Footer-socials-item">
                  <Image src={item.image} alt={`Remind on ${item.name}`} />
                </Link>
              ))}
            </div>
          </div>
          <div className="Footer-ikeepsafe--large">{iKeepSafe}</div>
        </div>
      </div>
      <div className="Footer-ikeepsafe--small">
        <div className="Footer-container">{iKeepSafe}</div>
      </div>
    </footer>
  );
}
